import React, {useContext, useState, useEffect, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import axios from 'axios';
import {Container, Dialog, DialogActions, DialogContent, Grid, Typography, Checkbox} from '@material-ui/core';

import GeolocInfo from 'components/GeolocInfo';
import Paiement from 'components/Paiement/Paiement';
import PayButton from 'components/Seances/PayButton';
import Topbar from 'components/Topbar';
import CartoucheSeance from 'components/CartoucheSeance';
import HighlightSection from 'components/HighlightSection';
import VODCarousel from 'components/VODCarousel';
import MediaLibraryCarousel from 'components/MediaLibraryCarousel';
import Context from 'utils/Context';
import Conf from 'utils/Conf';

import {axiosInstance} from 'utils/auth.service';

import './Cinema.scss';
import Pages from './Pages';
import FilmContent from './FilmContent';
import AvailableShows from './AvailableShows';

const parseWRMovie = film =>
  film ?? {
    ...film,
    film_id: film.id,
    teaser_id: film.teaser_id,
    infos_synopsis: film.synopsis,
    infos_sticker: film.sticker,
    infos_casting: film.casting,
    infos_note: film.note,
    infos_genre: film.genre,
    infos_presse: film.press,
    infos_producteur: film.producer,
    infos_realisateur: film.director,
    infos_duree: film.duration,
    infos_date_sortie: film.release_date,
    infos_annee_prod: film.production_date,
    infos_recompenses: film.rewards,
    affiche: film.affiche,
    distributeur_name: film.distributeur,
    nb_seances: null,
    local: null,
    video_provider: film.video_provider,
  };

const Cinema = ({params}) => {
  const {t} = useTranslation();

  const [paiementOpen, setPaiementOpen] = useState(false);
  const [cartoucheOpen, setCartoucheOpen] = useState(false);
  const [savedNewsletters, setSavedNewsletters] = useState([]);
  const [newsletters, setNewsletters] = useState([]);
  const [mediathequeVideos, setMediathequeVideos] = useState([]);
  const [seanceSelected, setSeanceSelected] = useState(null);
  const [VODs, setVODs] = useState([]);
  const [userRentals, setUserRentals] = useState([]);

  const {ed, appState, modele, goTo} = useContext(Context);

  const {film_id, cinema_id, prefixCinemaId} = params;

  const ref = React.createRef();

  const fetchNewsletters = useCallback(() => {
    axios.get(`${Conf.apiBaseUriLegacy}/newsletters`, {withCredentials: true}).then(res => {
      setSavedNewsletters(res.data);
      setNewsletters(res.data ? res.data.filter(n => n.cinema_id === parseInt(cinema_id)) : []);
    });
  }, [cinema_id]);

  const fetchMediatheque = useCallback(() => {
    axios
      .get(`${Conf.apiBaseUriLegacy}${film_id ? `/mediatheque_film/${film_id}` : `/mediatheque_cinema/${cinema_id}`}`, {
        withCredentials: true,
      })
      .then(res => {
        setMediathequeVideos(res.data);
      });
  }, [film_id, cinema_id]);

  const fetchVODs = useCallback(() => {
    axios
      .get(`${Conf.apiBaseUri}/cinemas/${cinema_id}/vod`)
      .then(res => {
        setVODs(res.data);
      })
      .catch(error => {
        console.error('Error fetchVod call:', error);
      });
  }, [cinema_id /*, searchString*/]);

  const isLoggedIn = !!appState.user?.email;
  useEffect(() => {
    if (!isLoggedIn || userRentals.length) return;
    axiosInstance.get('/users/rentals').then(data => {
      setUserRentals(data.data);
    });
  }, [isLoggedIn, userRentals]);

  useEffect(() => {
    fetchNewsletters();
    fetchMediatheque();
    fetchVODs();
  }, [fetchNewsletters, fetchMediatheque, fetchVODs]);

  const handleChangeNewslettersCinema = event => {
    const value = event.target.checked ? 1 : 0;
    const cinema = _.find(modele.cinemas, {cinema_id: parseInt(cinema_id)});
    const newsletter = newsletters[0];

    if (!newsletter) {
      //NEWSLETTER FIRST SUB
      //UPDATE DB
      const m = new Date();
      const dateString = `${m.getUTCFullYear()}-${m.getUTCMonth() +
        1}-${m.getUTCDate()} ${m.getUTCHours()}:${m.getUTCMinutes()}:${m.getUTCSeconds()}`;

      const formData = new FormData();
      formData.append('cinema_id', cinema_id);
      formData.append('optin', 1);
      formData.append('optin_date', dateString);
      axios
        .post(`${Conf.apiBaseUriLegacy}/newsletter`, formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          //UPDATE STATE
          fetchNewsletters();
        });
    } else if (newsletter) {
      //NEWSLETTER UPDATE
      //UPDATE DB
      const formData = new FormData();
      formData.append('optin', newsletter.optin ? 0 : 1);
      axios
        .post(`${Conf.apiBaseUriLegacy}/newsletter/${newsletter.id}`, formData, {
          withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          //UPDATE STATE
          const newSavedNewsletters = savedNewsletters.map(n =>
            n.cinema_name === cinema.cinema_name ? {...n, optin: value} : n,
          );
          setSavedNewsletters(newSavedNewsletters);
          setNewsletters(
            newSavedNewsletters ? newSavedNewsletters.filter(n => n.cinema_id === parseInt(cinema_id)) : [],
          );
        });
    }
  };

  const handlePaiementOpen = () => {
    if (appState.user.email) {
      setPaiementOpen(true);
      setCartoucheOpen(false);
    } else {
      ed.trigger('loginRegisterOpen');
    }
  };

  const handlePaiementClose = () => {
    setPaiementOpen(false);
  };

  const handleCartoucheOpen = seance => {
    setSeanceSelected(seance);
    setCartoucheOpen(true);
  };

  const handleCartoucheClose = c => {
    setSeanceSelected(null);
    setCartoucheOpen(false);
  };

  const shouldShowNewsletterCheckbox = appState.conf.NEWSLETTER_ENABLED;

  if (prefixCinemaId && prefixCinemaId !== cinema_id) {
    goTo(`/c/${prefixCinemaId}`);
  }

  const isEmbeded = window.location.pathname.startsWith('/c/');

  const cinema = _.find(modele.cinemas, {cinema_id: parseInt(cinema_id)});
  const seances = _.filter(modele.seances, {cinema_id: parseInt(cinema_id)});

  const vod = _.find(VODs, {film: {id: parseInt(film_id)}});

  let film = _.find(modele.films, {film_id: parseInt(film_id)});
  if (!film && vod) {
    film = parseWRMovie(vod.film);
  }

  const seancesCine = seances.filter(
    seance => seance.cinema_id === parseInt(params.cinema_id) && seance.billetterie === 1,
  );
  const seancesRencontre = seances.filter(
    seance => seance.cinema_id === parseInt(params.cinema_id) && seance.event_id > 0,
  );

  const seancesFilm = seancesCine.filter(seance => seance.film_id === parseInt(params.film_id));

  if (!cinema) {
    return <Typography variant="body2">Vous devez selectionner un cinema !</Typography>;
  }

  const bgStyle = cinema.background ? {backgroundImage: `url("${Conf.assetsRoot}${cinema.background}")`} : null;

  const profilePic =
    (cinema.logo && `${Conf.assetsRoot}${cinema.logo}`) ||
    (cinema.exploitant_picture && `${Conf.assetsRoot}${cinema.exploitant_picture}`);

  return (
    <Container maxWidth={false} ref={ref}>
      <Topbar />
      <div className={classNames('page-cinema', {'cinema-home': !film})}>
        <div className="page-cinema_background" style={bgStyle} />
        <div className="page-cinema_background-overlay" />
        <HighlightSection
          title={film ? film.film_name : cinema.cinema_name}
          location={film ? cinema.cinema_name : cinema.city_name}
          video={film ? film.teaser_id : cinema.jingle_id}
          profilePic={profilePic}
          cinemaBio={!film && cinema.description}
          onMoreInfosClick={
            !isEmbeded && cinema.pages.length > 0 ? () => window.scrollTo(0, document.body.scrollHeight) : null
          }
          tags={film ? [film.infos_duree, film.infos_genre, film.infos_date_sortie, film.infos_producteur] : []}
          onPreviousClick={() => goTo(film ? `/cinema/${cinema_id}` : '/')}
          onLocationClick={() => film && goTo(`/cinema/${cinema_id}`)}
        />
      </div>
      <div className="spacer" />
      {!!film && (
        <FilmContent
          film={film}
          currentVodFilm={vod}
          cinema={cinema}
          seances={seancesFilm}
          rencontres={seancesRencontre.filter(r => r.film_id === film.film_id)}
          onSeanceClick={handleCartoucheOpen}
        />
      )}
      <div className="spacer" />
      <AvailableShows
        seances={seancesCine}
        seancesRencontre={seancesRencontre}
        selectedFilmId={params.film_id ? parseInt(params.film_id) : null}
        onSeanceClick={handleCartoucheOpen}
      />
      <div className="spacer" />
      {VODs.length > 0 && (
        <VODCarousel vods={VODs} cinemaId={cinema_id} userRentals={userRentals} currentFilmId={film_id} />
      )}
      <div className="spacer" />

      <Container maxWidth="lg" className="cinema-pages">
        {mediathequeVideos && (
          <div className="page-cinema__content">
            <MediaLibraryCarousel mediatheque={mediathequeVideos} />
          </div>
        )}
        <div className="spacer" />
        <Pages pages={cinema.pages} />
      </Container>
      <Paiement open={paiementOpen} onClose={handlePaiementClose} seance={seanceSelected} />
      <Dialog open={cartoucheOpen} onClose={handleCartoucheClose} className="my-dialog">
        <DialogContent>{seanceSelected && <CartoucheSeance hideBtns seance={seanceSelected} />}</DialogContent>
        <DialogActions>
          {!!shouldShowNewsletterCheckbox && (
            <Grid container alignContent="center" alignItems="center">
              <Grid item xs={2} md={1}>
                <Checkbox
                  color="default"
                  checked={newsletters && newsletters.length > 0 && newsletters[0].optin === 1}
                  onChange={handleChangeNewslettersCinema}
                  name={cinema.cinema_name}
                  inputProps={{'aria-label': 'primary Checkbox'}}
                />
              </Grid>
              <Grid item xs={10} md={11}>
                {t(`newsletter-du-cinéma`) + ` «${cinema.cinema_name}»`}
              </Grid>
            </Grid>
          )}
          {seanceSelected && <PayButton showAccess onPay={() => handlePaiementOpen()} seance={seanceSelected} />}
        </DialogActions>
      </Dialog>
      <GeolocInfo />
    </Container>
  );
};

export default Cinema;
